import { observer } from 'mobx-react-lite'
import { Modal, Typography } from 'antd'
import { useNotification } from '@/Ship/Hooks/useNotification'
import { AssignmentFormFieldCell } from '../AssignmentFormFieldCell'
import { NumberFormFieldCell } from '../NumberFormFieldCell'
import { SimpleFormFieldCell } from '../SimpleFormFieldCell'
import { getUnitLabel } from '../../Helpers/unitLabel'
import {
    clusterCodeOf,
    regionFilterFor,
} from '@/Containers/AssignmentSection/AssignmentContainer/Helpers/clusterRegion'
import type { AddTaskModalStore } from '../../Stores/AddTaskModalStore'
import type { VehicleNeedsEditStore } from '../../Stores/VehicleNeedsEditStore'

const { Text } = Typography

interface Props {
    store: AddTaskModalStore
    editStore: VehicleNeedsEditStore
    /** Resolve the unit label for the executor whose modal is open (driven by `vehicleNeeds`). */
    vehicleNeeds: any[]
}

const errorStyle = { outline: '1px solid #ff4d4f', borderRadius: 6 }

export const AddTaskModal = observer(({ store, editStore, vehicleNeeds }: Props) => {
    const { showSuccess, showError } = useNotification()

    const unit = vehicleNeeds.find((v: any) =>
        (v.executors || []).some((e: any) => e.uuid === store.executorUuid)
    )?.unit

    return (
        <Modal
            title="Нове завдання"
            open={store.open}
            onOk={() => store.submit(editStore, { success: showSuccess, error: showError })}
            onCancel={() => store.close()}
            confirmLoading={store.loading}
            okText="Додати"
            cancelText="Скасувати"
            width={560}
            destroyOnClose
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
                {store.errors.length > 0 && (
                    <Text type="danger" style={{ fontSize: 12 }}>
                        Не заповнені обов'язкові поля
                    </Text>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                        <Text>
                            Кластер <span style={{ color: 'red' }}>*</span>:
                        </Text>
                        <div style={{ marginTop: 4, ...(store.errors.includes('cluster') ? errorStyle : {}) }}>
                            <AssignmentFormFieldCell
                                key={`edit-modal-cluster-${store.modalKey}`}
                                fieldName="cluster"
                                fieldDef={{
                                    type: 'select',
                                    resource: 'Setting.ListCluster',
                                    inputProps: { filter: { isActive: true } },
                                }}
                                initialValue={null}
                                initialIri={null}
                                onSave={(iri) => store.setCluster(iri)}
                                onSaveObject={(obj) => store.setClusterObj(obj)}
                                disabled={false}
                            />
                        </div>
                    </div>
                    <div>
                        <Text>
                            Регіон <span style={{ color: 'red' }}>*</span>
                            {!store.cluster ? (
                                <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, marginLeft: 6 }}>
                                    (спочатку оберіть кластер)
                                </span>
                            ) : (
                                ''
                            )}
                            :
                        </Text>
                        <div style={{ marginTop: 4, ...(store.errors.includes('region') ? errorStyle : {}) }}>
                            {/* key з кластером: зміна кластера перестворює поле з порожнім значенням і новим фільтром */}
                            <AssignmentFormFieldCell
                                key={`edit-modal-region-${store.modalKey}-${store.cluster ?? 'none'}`}
                                fieldName="region"
                                fieldDef={{
                                    type: 'select',
                                    resource: 'Setting.ListRegion',
                                    inputProps: { filter: regionFilterFor(clusterCodeOf(store.cluster)) },
                                }}
                                initialValue={null}
                                initialIri={null}
                                onSave={(iri) => store.setRegion(iri)}
                                onSaveObject={(obj) => store.setRegionObj(obj)}
                                disabled={!store.cluster}
                            />
                        </div>
                    </div>
                </div>
                <div>
                    <Text>Найманий ТЗ:</Text>
                    <div style={{ marginTop: 4 }}>
                        <SimpleFormFieldCell
                            key={`edit-modal-isHired-${store.modalKey}`}
                            fieldName="isHired"
                            fieldDef={{ type: 'switch' }}
                            initialValue={store.isHired}
                            onChange={(val) => store.setIsHired(!!val)}
                        />
                    </div>
                </div>
                {store.isHired && (
                    <div>
                        <Text>
                            Перевізник <span style={{ color: 'red' }}>*</span>:
                        </Text>
                        <div style={{ marginTop: 4, ...(store.errors.includes('carrier') ? errorStyle : {}) }}>
                            <AssignmentFormFieldCell
                                key={`edit-modal-carrier-${store.modalKey}`}
                                fieldName="carrier"
                                fieldDef={{
                                    type: 'select',
                                    resource: 'Setting.ListCarrier',
                                    inputProps: { filter: { isActive: true, isOwn: false } },
                                }}
                                initialValue={null}
                                initialIri={null}
                                onSave={(iri) => store.setCarrier(iri)}
                                onSaveObject={(obj) => store.setCarrierObj(obj)}
                                disabled={false}
                            />
                        </div>
                    </div>
                )}
                <div>
                    <Text>
                        Водій <span style={{ color: 'red' }}>*</span>
                        {store.isHired && !store.carrier ? (
                            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, marginLeft: 6 }}>
                                (спочатку оберіть перевізника)
                            </span>
                        ) : (
                            ''
                        )}
                        :
                    </Text>
                    <div style={{ marginTop: 4, ...(store.errors.includes('driver') ? errorStyle : {}) }}>
                        <AssignmentFormFieldCell
                            key={`edit-modal-driver-${store.modalKey}-${store.carrier ?? 'none'}`}
                            fieldName="driver"
                            fieldDef={{
                                type: 'select',
                                resource: 'Setting.User',
                                inputProps: {
                                    filter: {
                                        filterRoles: { op: 'like', value: 'ROLE_DRIVER' },
                                        status: 'active',
                                        ...(store.isHired && store.carrier
                                            ? { 'carrier.code': store.carrier.split('/').pop() }
                                            : {}),
                                    },
                                    responseLabel: (item: any) =>
                                        item?.fullName ||
                                        `${item?.lastName || ''} ${item?.firstName || ''}`.trim() ||
                                        '—',
                                },
                            }}
                            initialValue={null}
                            initialIri={null}
                            onSave={(iri) => store.setDriver(iri)}
                            onSaveObject={(obj) => store.setDriverObj(obj)}
                            disabled={store.isHired && !store.carrier}
                        />
                    </div>
                </div>
                <div>
                    <Text>
                        Держ. номер ТЗ <span style={{ color: 'red' }}>*</span>
                        {store.isHired && !store.carrier ? (
                            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, marginLeft: 6 }}>
                                (спочатку оберіть перевізника)
                            </span>
                        ) : (
                            ''
                        )}
                        :
                    </Text>
                    <div style={{ marginTop: 4, ...(store.errors.includes('vehicle') ? errorStyle : {}) }}>
                        <AssignmentFormFieldCell
                            key={`edit-modal-vehicle-${store.modalKey}-${store.carrier ?? 'none'}`}
                            fieldName="vehicle"
                            fieldDef={{
                                type: 'select',
                                resource: 'Transport.Vehicle',
                                inputProps: {
                                    filter: store.isHired && store.carrier ? { carrier: store.carrier } : {},
                                    responseLabel: (item: any) =>
                                        item?.licensePlate || item?.registrationNumber || item?.title || '—',
                                },
                            }}
                            initialValue={null}
                            initialIri={null}
                            onSave={(iri) => store.setVehicle(iri)}
                            onSaveObject={(obj) => store.setVehicleObj(obj)}
                            disabled={store.isHired && !store.carrier}
                        />
                    </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                        <Text>
                            Загальна маса перевезення, {getUnitLabel(unit)} <span style={{ color: 'red' }}>*</span>:
                        </Text>
                        <div
                            style={{ marginTop: 4, ...(store.errors.includes('totalWeightTonnes') ? errorStyle : {}) }}
                        >
                            <NumberFormFieldCell
                                fieldName="totalWeightTonnes"
                                initialValue={store.totalWeightTonnes}
                                onChange={(val) => store.setTotalWeightTonnes(val)}
                                inputProps={{ min: 0, step: 0.01, precision: 3, style: { width: '100%' } }}
                            />
                        </div>
                    </div>
                    <div>
                        <Text>
                            Кількість ходок <span style={{ color: 'red' }}>*</span>:
                        </Text>
                        <div style={{ marginTop: 4, ...(store.errors.includes('tripsCount') ? errorStyle : {}) }}>
                            <NumberFormFieldCell
                                fieldName="tripsCount"
                                initialValue={store.tripsCount}
                                onChange={(val) => store.setTripsCount(val)}
                                inputProps={{ min: 1, step: 1, precision: 0, style: { width: '100%' } }}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    )
})
